import useSWR from "swr";
import { MondayApiQueryInstance } from "../../services/monday/monday-service-ts";
import logger from "../../lib/logger";
import {
  Column,
  LinkedBoard,
  PersonsAndTeams,
} from "../../components/ItemViewSection/table.data.interface";
import type { ColumnValueType } from "../../services/monday/monday-query-interface";
import type { Item } from "../../services/monday/monday-query-interface";
import { isSimbManagedColumnTitle } from "../../utils/simbManagedColumns";

function isColumnIncluded(cv: ColumnValueType): boolean {
  return !isSimbManagedColumnTitle(cv.column?.title) && cv.type !== "subtasks";
}

function parseColumnValue(cv: ColumnValueType): Column["value"] {
  if (typeof cv.value === "string" && cv.value !== "") {
    try {
      const parsed = JSON.parse(cv.value);
      if (typeof parsed === "object" && parsed !== null && "personsAndTeams" in parsed) {
        return parsed.personsAndTeams ?? parsed.persons_and_teams ?? null;
      }
      return parsed;
    } catch {
      return cv.text ?? cv.value;
    }
  }
  if (cv.text !== undefined) return cv.text;
  return cv.value ?? null;
}

function mapPersonsAndTeams(
  persons_and_teams: ColumnValueType["persons_and_teams"],
): PersonsAndTeams[] | undefined {
  if (!persons_and_teams?.length) return undefined;
  return persons_and_teams.map((p) => ({
    kind: p.kind,
    id: typeof p.id === "number" ? p.id : parseInt(String(p.id), 10) || 0,
  }));
}

export function columnValueToColumn(
  cv: ColumnValueType,
  boardId: string,
  boardName: string,
): Column {
  const persons_and_teams = mapPersonsAndTeams(cv.persons_and_teams);

  const column: Column = {
    title: cv.column?.title ?? cv.id,
    type: cv.type ?? "text",
    value: parseColumnValue(cv),
    columnId: cv.id,
    boards: [{ boardId, boardName }],
  };

  if (cv.label !== undefined) column.label = cv.label;
  if (cv.label_style) column.label_style = cv.label_style;
  if (persons_and_teams?.length) column.persons_and_teams = persons_and_teams;
  if (cv.values?.length) column.values = cv.values;
  if (cv.linked_item_ids?.length) column.linked_item_ids = cv.linked_item_ids;
  if (cv.linked_items?.length) column.linked_items = cv.linked_items;
  if (cv.tag_ids?.length) column.tag_ids = cv.tag_ids;
  if (cv.text) column.text = cv.text;

  return column;
}

function itemToColumns(
  item: Item,
  linkedByItemId: Map<string, LinkedBoard>,
): { boardId: string; columns: Column[] } | null {
  const boardId = item.board?.id != null ? String(item.board.id) : "";
  const linked = linkedByItemId.get(String(item.id));
  const boardName = linked?.boardName ?? "";

  if (!boardId || !item.column_values?.length) {
    return boardId ? { boardId, columns: [] } : null;
  }

  const columns = item.column_values
    .filter(isColumnIncluded)
    .map((cv) => columnValueToColumn(cv, boardId, boardName));

  return { boardId, columns };
}

/**
 * Fetches column values for linked items via getItemColumnValues (by item id), so we always
 * get the exact linked item regardless of board size. Maps results back to columns per board.
 */
async function fetchUnsharedBoardColumnsFromItems(
  linkedBoards: LinkedBoard[],
): Promise<Record<string, Column[]>> {
  if (!linkedBoards?.length) return {};

  const itemIds = linkedBoards.map((b) => parseInt(b.itemId, 10)).filter((id) => !Number.isNaN(id));
  if (!itemIds.length) return {};

  const linkedByItemId = new Map(linkedBoards.map((b) => [String(b.itemId), b]));

  try {
    const items: Item[] | undefined = await MondayApiQueryInstance.getItemColumnValues(itemIds);
    const result: Record<string, Column[]> = {};

    if (!items?.length) return result;

    for (const item of items) {
      const entry = itemToColumns(item, linkedByItemId);
      if (entry) result[entry.boardId] = entry.columns;
    }

    return result;
  } catch (err) {
    logger.error("Error fetching item column values for unshared columns", { error: err });
    return {};
  }
}

/**
 * Hook to load columns (with values) for "other" linked boards.
 * Uses getItemColumnValues(linkedBoard.itemId) so we get the exact linked item per board.
 */
function useUnsharedBoardColumns(linkedBoards: LinkedBoard[] | null | undefined) {
  const boardIdsKey = linkedBoards?.map((b) => `${b.boardId}:${b.itemId}`).join(",") ?? "";
  const cacheKey =
    linkedBoards?.length && boardIdsKey ? `unsharedBoardColumns:${boardIdsKey}` : null;

  const { data: columnsByBoardId, ...rest } = useSWR<Record<string, Column[]>>(
    cacheKey,
    () => fetchUnsharedBoardColumnsFromItems(linkedBoards!),
    {
      revalidateOnFocus: false,
      dedupingInterval: 60_000,
      keepPreviousData: true,
    },
  );

  /**
   * Refetches but keeps what's on screen if the fetch fails (the fetcher swallows errors and
   * returns `{}`, which would otherwise empty the section).
   */
  const refreshQuietly = () =>
    rest.mutate(
      async (current) => {
        const fresh = await fetchUnsharedBoardColumnsFromItems(linkedBoards ?? []);
        return Object.keys(fresh).length ? fresh : current;
      },
      { revalidate: false },
    );

  return {
    columnsByBoardId: columnsByBoardId ?? {},
    refreshQuietly,
    ...rest,
  };
}

export default useUnsharedBoardColumns;
